<template>
  <div class="flex flex-col w-full gap-4">

    <!-- Encabezado: capa origen (de dónde se copia) -->
    <div class="text-sm">
      <p class="text-gray-500 uppercase tracking-wide text-xs mb-1">
        {{ $t('source_layer') }}
      </p>
      <p class="font-medium text-gray-900">
        {{ layer.name }} <span class="text-gray-400">({{ layer.code }})</span>
      </p>
    </div>

    <!-- Sin destinos posibles → estado vacío -->
    <div v-if="targetLayers.length === 0"
      class="text-center p-8 rounded-md border border-dashed border-gray-300 text-sm text-gray-500">
      {{ $t('no_target_layers_available') }}
    </div>

    <template v-else>

      <!-- ═══ VISTA "Formulario" — Mientras no haya resultados ═══ -->
    <template v-if="results.length === 0">

      <!-- Selección de capas destino -->
      <div>
        <p class="text-gray-500 uppercase tracking-wide text-xs mb-2">
          {{ $t('target_layers') }}
        </p>

        <div class="flex items-center mb-2 pb-2 border-b border-gray-100">
          <b-checkbox v-model="selectAll" @input="_OnToggleSelectAll" size="is-small">
            <span class="font-medium text-sm">{{ $t('select_all') }}</span>
          </b-checkbox>
          <span class="ml-auto text-xs text-gray-500">
            {{ selectedTargetIds.length }} / {{ targetLayers.length }}
          </span>
        </div>

        <div class="flex flex-col gap-1 max-h-64 overflow-y-auto pr-1">
          <label v-for="target in targetLayers" :key="target.id"
            class="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer">
            <b-checkbox v-model="selectedTargetIds" :native-value="target.id" size="is-small" />
            <span class="text-sm text-gray-800">{{ target.name }}</span>
            <span class="text-xs text-gray-400">({{ target.code }})</span>
            <span v-if="target.has_progress"
              class="ml-auto text-xs px-2 py-0.5 rounded-full bg-orange-lightest text-orange-darker border border-orange-lighter">
              {{ $t('has_progress') }}
            </span>
          </label>
        </div>
      </div>

      <!-- Opciones de qué replicar (igual que antes) -->
      <div>
        <p class="text-gray-500 uppercase tracking-wide text-xs mb-2">
          {{ $t('what_to_replicate') }}
        </p>
        <div class="flex flex-col gap-2 px-1">
          <b-checkbox v-model="options.apply_form_config" size="is-small">
            <span class="text-sm">{{ $t('replicate_form_config') }}</span>
          </b-checkbox>
          <b-checkbox v-model="options.apply_simultaneous_entry" size="is-small">
            <span class="text-sm">{{ $t('replicate_simultaneous_entry') }}</span>
          </b-checkbox>
          <b-checkbox v-if="showActionsPermitted" v-model="options.apply_actions_permitted" size="is-small">
            <span class="text-sm">{{ $t('replicate_actions_permitted') }}</span>
          </b-checkbox>
          <div v-if="showStatusesOption" class="flex flex-col">
            <b-checkbox v-model="options.apply_statuses" size="is-small" :disabled="!options.apply_form_config">
              <span class="text-sm">{{ $t('replicate_statuses') }}</span>
            </b-checkbox>
            <span v-if="!options.apply_form_config"
              class="text-xs text-gray-400 italic ml-7 mt-0.5">
              {{ $t('requires_form_config') }}
            </span>
          </div>
          <div v-if="showIncidentsOption" class="flex flex-col">
            <b-checkbox v-model="options.apply_incidents" size="is-small" :disabled="!options.apply_form_config">
              <span class="text-sm">{{ $t('replicate_incidents') }}</span>
            </b-checkbox>
            <span v-if="!options.apply_form_config"
              class="text-xs text-gray-400 italic ml-7 mt-0.5">
              {{ $t('requires_form_config') }}
            </span>
          </div>
        </div>
      </div>

      <!-- Aviso warning -->
      <div v-if="warnSelectedHasProgress"
        class="flex items-start gap-2 p-3 rounded-md bg-orange-lightest border border-orange-lighter text-xs text-orange-darker">
        <b-icon pack="fas" icon="exclamation-triangle" size="is-small" />
        <span>{{ $t('bulk_apply_warning_has_progress') }}</span>
      </div>

      <!-- Footer: Aplicar -->
      <div class="flex justify-end gap-2 border-t pt-4">
        <b-button v-if="isApplying" loading size="is-small" type="is-primary">
          {{ $t('applying') }}
        </b-button>
        <b-button v-else
          size="is-small"
          type="is-primary"
          icon-pack="fas"
          icon-left="copy"
          :disabled="!canApply"
          @click="_HandleApply">
          {{ $t('apply') }}
        </b-button>
      </div>

      </template>

      <!-- ═══ VISTA "Resultados" — Cuando el backend responde OK ═══ -->
      <template v-else>

      <!-- Tabla de feedback -->
      <div>
        <p class="text-gray-500 uppercase tracking-wide text-xs mb-2">
          {{ $t('results') }}
        </p>
        <div class="flex flex-col gap-1">
          <div v-for="r in results" :key="r.layer_id"
            class="flex items-center gap-2 px-3 py-2 rounded border border-gray-100 bg-white text-sm">
            <b-icon :icon="_GetOutcomeIcon(r.outcome)" pack="fas" size="is-small"
              :class="_GetOutcomeIconClass(r.outcome)" />
            <span class="font-medium">{{ r.name }}</span>
            <span class="text-xs text-gray-500">({{ r.code }})</span>
            <span class="ml-auto text-xs px-2 py-0.5 rounded-full"
              :class="_GetOutcomeBadgeClass(r.outcome)">
              {{ _GetOutcomeLabel(r.outcome) }}
            </span>
          </div>
        </div>
      </div>

      <!-- Footer: Cerrar -->
      <div class="flex justify-end gap-2 border-t pt-4">
        <b-button size="is-small" type="is-primary" icon-pack="fas" icon-left="times" @click="_HandleClose">
          {{ $t('close') }}
        </b-button>
      </div>

      </template>

    </template>
  </div>
</template>

<script>
import { bulkApplyLayerConfig } from '../../services/pictograms.service.js';

export default {
  name: 'LayerBulkActions',

  inject: ['CurrentPictogram'],

  props: {
    // Capa actual — fuente de la replicación. Viene del registro
    // config-layer-options en el navigation tree.
    layer: { type: Object, required: true },
    // Opciones precheckeadas (opcional). Útil cuando se invoca el componente
    // desde un contexto específico (ej. modal desde "Estados de progreso" =
    // { apply_form_config: true, apply_statuses: true }).
    presetOptions: { type: Object, default: null },
    // Contexto desde donde se invoca el componente. Filtra opciones y backend:
    //   'all'      — desde el árbol "Acciones masivas". Muestra y copia TODO.
    //   'progress' — desde "Estados de progreso". Solo opciones/copy de progress.
    //   'incident' — desde "Estados de incidencia". Solo opciones/copy de incident.
    context: {
      type: String,
      default: 'all',
      validator: v => ['all', 'progress', 'incident'].includes(v),
    },
  },

  data() {
    return {
      selectedTargetIds: [],
      options: this.presetOptions
        ? { ...this._defaultOptions(), ...this.presetOptions }
        : this._defaultOptions(),
      isApplying: false,
      results: [],
    };
  },

  computed: {
    pictogram() {
      // CurrentPictogram es una función inyectada por el padre (pictogram-admin-panel)
      // para que sea reactiva. La llamamos para obtener el valor actual.
      return this.CurrentPictogram?.();
    },

    /**
     * Las otras capas del pictograma (excluyendo la actual).
     * Para cada una, derivamos has_progress desde layer.lines que ya viene
     * cargado por el endpoint /pictogram/{id} — sin llamadas extra.
     */
    targetLayers() {
      const all = this.pictogram?.layers ?? [];
      return all
        .filter(l => l.id !== this.layer.id)
        .map(l => ({
          id:           l.id,
          code:         l.code,
          name:         l.name,
          has_progress: (l.lines || []).length > 0,
        }));
    },

    selectAll: {
      get() {
        return this.targetLayers.length > 0
          && this.selectedTargetIds.length === this.targetLayers.length;
      },
      set(value) {
        this._OnToggleSelectAll(value);
      },
    },

    /**
     * True si alguna capa seleccionada tiene avance Y se pidió replicar
     * estados/grupos. Muestra el warning amarillo.
     */
    warnSelectedHasProgress() {
      const wantsStatuses = this.options.apply_statuses
        || this.options.apply_incidents;
      if (!wantsStatuses) return false;
      const selectedSet = new Set(this.selectedTargetIds);
      return this.targetLayers.some(l => selectedSet.has(l.id) && l.has_progress);
    },

    canApply() {
      const hasTargets = this.selectedTargetIds.length > 0;
      const hasOption  = this.options.apply_form_config
        || this.options.apply_simultaneous_entry
        || this.options.apply_actions_permitted
        || this.options.apply_statuses
        || this.options.apply_incidents;
      return hasTargets && hasOption;
    },

    showStatusesOption()    { return this.context !== 'incident'; },
    showIncidentsOption()   { return this.context !== 'progress'; },
    // Acciones permitidas existen tanto en progress como en incident
    // (subtree metadata.config.statuses.{progress|incidents}.actions),
    // por lo que el checkbox se muestra en todos los contextos.
    showActionsPermitted()  { return true; },
  },

  watch: {
    'options.apply_form_config'(newVal) {
      if (!newVal) {
        this.options.apply_statuses  = false;
        this.options.apply_incidents = false;
      }
    },
  },

  methods: {
    _defaultOptions() {
      return {
        apply_form_config:        true,
        apply_simultaneous_entry: false,
        apply_actions_permitted:  false,
        apply_statuses:           false,
        apply_incidents:          false,
      };
    },

    _OnToggleSelectAll(value) {
      this.selectedTargetIds = value
        ? this.targetLayers.map(l => l.id)
        : [];
    },

    _HandleApply() {
      // Si involucra cambios destructivos (grupos/estados) confirmamos.
      const isDestructive = this.options.apply_statuses
        || this.options.apply_incidents;

      if (isDestructive) {
        this.$buefy.dialog.confirm({
          title:       this.$t('bulk_apply_confirm_title'),
          message:     this.$t('bulk_apply_confirm_message', { n: this.selectedTargetIds.length }),
          confirmText: this.$t('apply'),
          cancelText:  this.$t('cancel'),
          type:        'is-warning',
          hasIcon:     true,
          onConfirm:   () => this._DoApply(),
        });
      } else {
        this._DoApply();
      }
    },

    async _DoApply() {
      this.isApplying = true;
      this.results = [];

      try {
        const response = await bulkApplyLayerConfig(this.pictogram.id, {
          source_layer_id:  this.layer.id,
          target_layer_ids: this.selectedTargetIds,
          context:          this.context,
          options:          { ...this.options },
        });

        if (response?.success) {
          this.results = response.results || [];
          this.$toast.open({
            message:  this.$t('bulk_apply_success'),
            type:     'is-success',
            position: 'is-top-right',
          });
          this.selectedTargetIds = [];
          // NOTA: no emitimos 'close' acá — queremos que el usuario vea la tabla
          // de resultados antes de decidir cerrar. El botón "Cerrar" lo dispara.
        } else {
          this.$toast.open({
            message:  response?.error_msg || this.$t('bulk_apply_error'),
            type:     'is-danger',
            position: 'is-top-right',
          });
        }
      } catch (err) {
        this.$toast.open({
          message:  err.error_msg || err.message || this.$t('bulk_apply_error'),
          type:     'is-danger',
          position: 'is-top-right',
        });
      } finally {
        this.isApplying = false;
      }
    },

    _HandleClose() {
      this.$emit('close', { results: this.results });
    },

    // ─── Helpers visuales para la tabla de resultados ─────────────────────

    _GetOutcomeLabel(outcome) {
      return this.$t('bulk_outcome_' + outcome);
    },

    _GetOutcomeIcon(outcome) {
      switch (outcome) {
        case 'created':              return 'check-circle';
        case 'replaced':             return 'sync-alt';
        case 'skipped_has_progress': return 'exclamation-circle';
        case 'config_only':          return 'cog';
        default:                     return 'info-circle';
      }
    },

    _GetOutcomeIconClass(outcome) {
      switch (outcome) {
        case 'created':              return 'text-green-dark';
        case 'replaced':             return 'text-blue-dark';
        case 'skipped_has_progress': return 'text-orange-dark';
        case 'config_only':          return 'text-grey-dark';
        default:                     return 'text-grey-dark';
      }
    },

    _GetOutcomeBadgeClass(outcome) {
      switch (outcome) {
        case 'created':
          return 'bg-green-lightest text-green-dark border border-green-lighter';
        case 'replaced':
          return 'bg-blue-lightest text-blue-dark border border-blue-lighter';
        case 'skipped_has_progress':
          return 'bg-orange-lightest text-orange-darker border border-orange-lighter';
        case 'config_only':
          return 'bg-grey-lighter text-grey-darker border border-grey-light';
        default:
          return 'bg-grey-lighter text-grey-darker';
      }
    },
  },
};
</script>
